import React, { useState, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { APP_VERSION } from '../../constants/appConstants';
import { Button } from '../../components/buttons/button';
import { AuthContext } from '../../context/AuthContext';
import { useSync } from '../../hooks/useSync';
import { openConfirmationModal } from '../../reducers/uiSlice';
import { confirmationService } from '../../services/confirmationService';
import ToggleSwitch from '../../components/ui/ToggleSwitch';
import UserProfile from '../../components/ui/UserProfile';
import ImageZoomModal from '../../components/modals/ImageZoomModal';
import PageLayout from '../../components/layout/PageLayout';
import { useSettings } from '../../hooks/useSettings';
import SettingsSection from '../../components/ui/SettingsSection';
import SettingsItem from '../../components/ui/SettingsItem';
import { SyncStatusIcon, StorageIcon, EditIcon, AlertCircleIcon, InfoIcon, ThemeIcon, PasswordIcon, HelpSupportIcon, OfflineFileIcon, LastSyncIcon } from '../../components/Icons/svgIcons';
import { ICONS } from "../../constants/imageConstants";

const Settings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const { isOnline, lastSyncTime } = useSync();
  const [isZoomModalOpen, setIsZoomModalOpen] = useState(false);
  const [zoomedImageUrl, setZoomedImageUrl] = useState('');

  const {
    autoSync,
    storageInfo,
    loadingAutoSync,
    handleAutoSyncToggle,
    handleClearLocalData
  } = useSettings();

  const handleImageClick = (imageUrl) => {
    setZoomedImageUrl(imageUrl);
    setIsZoomModalOpen(true);
  };

  const handleLogout = () => {
    const confirmId = 'logout';
    confirmationService.register(confirmId, () => logout());
    dispatch(openConfirmationModal({
      title: 'Confirm Logout',
      message: 'Are you sure you want to logout? Unsynced data will be lost.',
      type: 'logout',
      confirmId
    }));
  };

  const formatLastSyncTime = timestamp => {
    if (!timestamp) return 'Never';
    try {
      return new Date(timestamp).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return 'Never';
    }
  };

  const iconBaseClass = "w-5 h-5 icon-theme";
  const iconBgBase = "icon-bg-theme";

  return (
    <>
      <ImageZoomModal 
        isOpen={isZoomModalOpen}
        onClose={() => setIsZoomModalOpen(false)}
        imageUrl={zoomedImageUrl}
      />
      <PageLayout title="Settings" showBackButton showStatus>
        <div className="space-y-4">
          <div className="bg-white rounded-xl border-2 border-gray-200 p-5 shadow-sm">
            <div className="flex justify-between items-center">
              <UserProfile user={user} onImageClick={handleImageClick} />
              <button 
                onClick={() => navigate('/profile')} 
                className="inline-flex items-center justify-center h-9 px-4 text-sm font-semibold rounded-md border bg-white active:scale-[0.98] transition-all duration-200 border-[var(--btn-secondary-bg)] text-[var(--btn-secondary-bg)] hover:bg-[var(--btn-secondary-bg)]/5"
              >
                <EditIcon className="w-4 h-4 mr-1.5 text-[var(--btn-secondary-bg)]" />
                Edit
              </button>
            </div>
          </div>

          <SettingsSection title="Sync Configuration" subtitle="Manage data synchronization">
            <SettingsItem
              label="Last Synced"
              value={formatLastSyncTime(lastSyncTime)}
              showChevron={false}
              icon={<LastSyncIcon className={iconBaseClass} />}
              iconBg={iconBgBase}
            />
            <SettingsItem
              label="Auto Sync"
              description="Automatically sync when online"
              showChevron={false}
              icon={<SyncStatusIcon className={iconBaseClass} />}
              iconBg={iconBgBase}
              rightContent={
                <ToggleSwitch 
                  checked={autoSync} 
                  onChange={handleAutoSyncToggle} 
                  disabled={loadingAutoSync}
                />
              }
            />
          </SettingsSection>

          <SettingsSection title="Account & Security" subtitle="Manage your account settings">
            <SettingsItem
              label="Change Password"
              description="Update your password"
              className="cursor-pointer"
              onClick={() => navigate('/profile/change-password')}
              icon={<PasswordIcon className={iconBaseClass} />}
              iconBg={iconBgBase}
            />
          </SettingsSection>

          <SettingsSection title="Appearance" subtitle="Customize look and feel">
            <SettingsItem
              label="App Theme"
              description="Change colors and layout style"
              onClick={() => navigate('/settings/theme')}
              icon={<ThemeIcon className={iconBaseClass} />}
              iconBg={iconBgBase}
            />
          </SettingsSection>

          <SettingsSection title="Storage & Data" subtitle="Manage offline content">
            <SettingsItem
              label="Offline Files"
              description={`${storageInfo.itemCount} items stored locally`}
              showChevron={false}
              icon={<OfflineFileIcon className={iconBaseClass} />}
              iconBg={iconBgBase}
            />
            <SettingsItem
              label="Storage Usage"
              description="Total space used"
              value={`${storageInfo.usedMB.toFixed(1)} MB`}
              showChevron={false}
              icon={<StorageIcon className={iconBaseClass} />}
              iconBg={iconBgBase}
            />
            <div className="p-4">
              <button 
                onClick={handleClearLocalData} 
                disabled={!isOnline} 
                className="inline-flex items-center justify-center h-10 px-4 text-sm font-semibold rounded-lg w-full bg-red-50 text-red-600 border border-red-200 active:bg-red-100 active:border-red-300 transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none hover:bg-red-100"
              >
                <AlertCircleIcon className="w-4 h-4 mr-2 text-red-600" />
                Clear Local Data
              </button>
            </div>
          </SettingsSection>

          <SettingsSection title="Support & Information" subtitle="Get help and app details">
            <SettingsItem
              label="Help & Support"
              description="FAQs and contact support"
              icon={<HelpSupportIcon className={iconBaseClass} />}
              iconBg={iconBgBase}
            />
            <SettingsItem
              label="About Terriqon"
              description="App version and details"
              rightContent={<span className="text-xs font-bold text-[var(--text-main)] bg-gray-100 px-2.5 py-1 rounded-md">{APP_VERSION}</span>}
              icon={<InfoIcon className={iconBaseClass} />}
              iconBg={iconBgBase}
            />
          </SettingsSection>

          <div className="pt-2">
            <Button onClick={handleLogout} variant="danger" size="large" fullWidth>
              <img src={ICONS.LOGOUT} className='mr-2 w-5 h-5 brightness-0 invert'/>
              Logout
            </Button>
            <p className="text-center text-xs text-gray-500 mt-4 font-medium">
              Terriqon Field Data Collector v{APP_VERSION}
            </p>
          </div>
        </div>
      </PageLayout>
    </>
  );
};

export default Settings;